import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  useToast,
  useColorMode,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Link,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";

const EMAIL_MAX_LEN = 254;

const isValidEmail = (value) => {
  if (!value) {
    return false;
  }

  const email = value.trim();

  if (email.length > EMAIL_MAX_LEN) {
    return false;
  }

  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
};

const EmailModal = ({ walletAddress, isOpen, onClose, onRegistered }) => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(true);

  const toast = useToast();
  const { colorMode } = useColorMode();

  useEffect(() => {
    let cancelled = false;

    const checkEmailBeforeOpen = async () => {
      if (!walletAddress) {
        return;
      }

      try {
        const account = await apiFetch(
          `/app/account?wallet=${encodeURIComponent(
            walletAddress.toLowerCase(),
          )}`,
        );

        if (!cancelled && account.registered && account.user?.hasEmail) {
          onRegistered();
          onClose();
        }
      } catch (error) {
        if (!cancelled) {
          console.error(
            "Failed to check account email:",
            error?.message || error,
          );
        }
      }
    };

    if (isOpen && walletAddress) {
      checkEmailBeforeOpen();
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, walletAddress, onClose, onRegistered]);

  const handleSubmit = async () => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!isValidEmail(normalizedEmail) || !walletAddress) {
      setTouched(true);

      return toast({
        title: "Invalid email",
        description:
          "Please enter a valid email address (e.g., user@example.com).",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    if (!acceptedTerms) {
      return toast({
        title: "Please accept the Terms",
        description: "You must accept the Terms of Service to continue.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }

    const normalizedWallet = walletAddress.trim().toLowerCase();

    setLoading(true);

    try {
      const result = await apiFetch("/register", {
        method: "POST",
        body: JSON.stringify({
          walletAddress: normalizedWallet,
          email: normalizedEmail,
          emailSubscribed,
          acceptedTerms,
        }),
      });

      if (!result.success) {
        toast({
          title: "Registration Failed",
          description: result.message,
          status: result.message?.includes("used") ? "info" : "error",
          duration: 3000,
          isClosable: true,
        });

        if (result.message?.includes("used")) {
          onRegistered();
          onClose();
        }

        return;
      }

      toast({
        title: "Registered!",
        description: "Email saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onRegistered();
      onClose();
    } catch (error) {
      toast({
        title: "Unexpected Error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const showError = touched && !isValidEmail(email);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />

      <ModalContent
        bg={
          colorMode === "light"
            ? "rgba(255,255,255,0.6)"
            : "rgba(255,255,255,0.1)"
        }
        border="1px solid rgba(255,255,255,0.2)"
        borderRadius="2xl"
        backdropFilter="blur(20px)"
        boxShadow={
          colorMode === "light"
            ? "0 4px 40px rgba(0,0,0,0.1)"
            : "0 4px 40px rgba(0,0,0,0.3)"
        }
        color={colorMode === "light" ? "gray.800" : "white"}
      >
        <ModalHeader>Enter Your Email</ModalHeader>

        <ModalBody>
          <FormControl isInvalid={showError} mb={3}>
            <Input
              placeholder="satoshi@example.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);

                if (!touched) {
                  setTouched(true);
                }
              }}
              onBlur={() => setTouched(true)}
              type="email"
              autoComplete="email"
              inputMode="email"
            />

            <FormErrorMessage>
              Please enter a valid email address.
            </FormErrorMessage>
          </FormControl>

          <Checkbox
            isChecked={emailSubscribed}
            onChange={(event) => setEmailSubscribed(event.target.checked)}
            mb={2}
          >
            Send notifications using email
          </Checkbox>

          <Checkbox
            isChecked={acceptedTerms}
            onChange={(event) => setAcceptedTerms(event.target.checked)}
          >
            I have read and accept the{" "}
            <Link href="/terms" color="teal.300" isExternal>
              Terms of Service
            </Link>
          </Checkbox>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={loading || !isValidEmail(email) || !acceptedTerms}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmailModal;
